import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackInternalEvent } from '@rio/tracking'
import { useDispatch } from 'react-redux'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useI18n } from 'builder/hooks/useI18n'
import { actions, selectors } from 'builder/modules/resumeDistribution'
import { Store } from 'builder/modules/store'
import { useResume } from 'builder/hooks/useResume'
import { formatDocumentPreviewUrl } from 'builder/utils/formatDocumentPreviewUrl'
import { ButtonTheme } from 'builder/components/Button'
import { TooltipV2 } from 'builder/components/Tooltip'
import {
  Container,
  Title,
  Content,
  Description,
  Subtitle,
  ItemsContainer,
  Item,
  ItemContainer,
  ButtonsContainer,
  PremiumButton,
  CancelSubcriptionText,
  Icon,
  Header,
  ResumeDistributionContainer,
  RecruitersAmount,
  Resume,
  ResumeDistributionHeader,
  RecruitersAmountContainer,
  RecruitersAmountDescriptionContainer,
  RecruitersAmountDescription,
  ResumeDistributionRecruiters,
  RecruiterContainer,
  RecruiterName,
  RecruiterCompany,
  TickContainer,
  Tick,
  ViewRecruitersButton,
  Items,
} from './styles'
import DOCUMENTS_ICON from './assets/icons/documents.svg'
import ARROW_TARGET_ICON from './assets/icons/arrow_target.svg'
import PEOPLE_ICON from './assets/icons/people.svg'
import PENCIL_ICON from './assets/icons/pencil.svg'
import QUESTION_ICON from './assets/icons/question.svg'

interface SubscriptionCancellationResumeDistributionProps {
  onContinueCancellation: () => void
}

const ITEMS = (t: (key: string) => string) => [
  {
    src: DOCUMENTS_ICON,
    text: t('builder.subscription_cancellation_v4.resume_distribution.enabled.items.one'),
  },
  {
    src: PEOPLE_ICON,
    text: t('builder.subscription_cancellation_v4.resume_distribution.enabled.items.two'),
  },
  {
    src: PENCIL_ICON,
    text: t('builder.subscription_cancellation_v4.resume_distribution.enabled.items.three'),
  },
  {
    src: ARROW_TARGET_ICON,
    text: t('builder.subscription_cancellation_v4.resume_distribution.enabled.items.four'),
  },
]

const ItemsComponent = ({ isMobile, t }: { isMobile: boolean; t: (key: string) => string }) => (
  <Items $isMobile={isMobile}>
    <Subtitle>
      {t('builder.subscription_cancellation_v4.resume_distribution.enabled.subtitle')}
    </Subtitle>
    <ItemsContainer>
      {ITEMS(t).map(item => (
        <ItemContainer key={item.src}>
          <Icon src={item.src} />
          <Item>{item.text}</Item>
        </ItemContainer>
      ))}
    </ItemsContainer>
  </Items>
)

export const SubscriptionCancellationResumeDistribution = ({
  onContinueCancellation,
}: SubscriptionCancellationResumeDistributionProps) => {
  const navigate = useNavigate()
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const distributedRecruiterCount = useTypedSelector(selectors.distributedRecruiterCount)
  const distributionDates = useTypedSelector(selectors.distributionDates)
  const recruiters = useTypedSelector((state: Store) =>
    selectors.recruiters(state, distributionDates?.length ? distributionDates[0].unixFormat : 0),
  )
  const candidate = useTypedSelector(selectors.candidate)
  const resume = useResume(candidate?.resumeId || null)
  const distributionWeek = candidate?.distributionWeek || 0

  useEffect(() => {
    dispatch(actions.fetchDistributedRecruitersCount())
  }, [dispatch])

  useEffect(() => {
    if (distributionDates?.length) {
      dispatch(actions.fetchRecruiters({ date: distributionDates[0].unixFormat }))
    }
  }, [dispatch, distributionDates])

  useEffect(() => {
    trackInternalEvent('see_rd_page', { type: 'active' })
  }, [])

  const getThumbnailUrl = () => {
    if (!resume) return
    return formatDocumentPreviewUrl(resume)
  }

  const weeksLeft = 4 - distributionWeek

  return (
    <Container>
      <Header>{i18n.t('builder.subscription_cancellation_v4.auto_apply.header')}</Header>
      <Content>
        <Description>
          <Title>
            {i18n.t(
              'builder.subscription_cancellation_v4.resume_distribution.enabled.title_line_one',
            )}
          </Title>
          <ItemsComponent isMobile={false} t={i18n.t} />
        </Description>
        <ResumeDistributionContainer
          onClick={() => {
            trackInternalEvent('promo_acceptance', { promo: 'RD', where: 'cancellation' })
            navigate('/resume-distribution')
          }}
        >
          <ResumeDistributionHeader>
            <RecruitersAmountContainer>
              <RecruitersAmount>
                {i18n.t(
                  'builder.subscription_cancellation_v4.resume_distribution.enabled.recruiters_amount',
                  { amount: distributedRecruiterCount },
                )}
              </RecruitersAmount>
              <RecruitersAmountDescriptionContainer>
                <RecruitersAmountDescription>
                  {i18n.t(
                    'builder.subscription_cancellation_v4.resume_distribution.enabled.already_received',
                  )}
                </RecruitersAmountDescription>
                <TooltipV2
                  content={i18n.t(
                    'builder.subscription_cancellation_v4.resume_distribution.enabled.tooltip',
                    { distributed_recruiter_count: distributedRecruiterCount },
                  )}
                  multiline
                  width={287}
                >
                  <Icon
                    src={QUESTION_ICON}
                    style={{ cursor: 'pointer' }}
                    onClick={event => event.stopPropagation()}
                  />
                </TooltipV2>
              </RecruitersAmountDescriptionContainer>
              <RecruitersAmount $withMarginTop>
                {i18n.t(
                  'builder.subscription_cancellation_v4.resume_distribution.enabled.recruiters_amount_aprox',
                  { amount: weeksLeft * 50 },
                )}
              </RecruitersAmount>
              <RecruitersAmountDescriptionContainer>
                <RecruitersAmountDescription>
                  {i18n.t(
                    'builder.subscription_cancellation_v4.resume_distribution.enabled.next_scheduled',
                  )}
                </RecruitersAmountDescription>
              </RecruitersAmountDescriptionContainer>
            </RecruitersAmountContainer>
            <Resume thumbnail={getThumbnailUrl()} />
          </ResumeDistributionHeader>
          <ResumeDistributionRecruiters>
            {recruiters?.data.slice(0, 3).map(recruiter => (
              <RecruiterContainer key={recruiter.name}>
                <RecruiterName>{recruiter.name}</RecruiterName>
                <RecruiterCompany>{recruiter.company}</RecruiterCompany>
                <TickContainer>
                  <Tick />
                </TickContainer>
              </RecruiterContainer>
            ))}
          </ResumeDistributionRecruiters>

          <ViewRecruitersButton
            theme={ButtonTheme.secondary}
            onClick={() => {
              trackInternalEvent('promo_acceptance', { promo: 'RD', where: 'cancellation' })
              navigate('/resume-distribution')
            }}
          >
            {i18n.t('builder.subscription_cancellation_v4.resume_distribution.enabled.view_all')}
          </ViewRecruitersButton>
        </ResumeDistributionContainer>

        <ItemsComponent isMobile t={i18n.t} />
      </Content>
      <ButtonsContainer>
        <PremiumButton
          onClick={() => {
            trackInternalEvent('promo_acceptance', { promo: 'RD', where: 'cancellation' })
            navigate('/resume-distribution')
          }}
        >
          {i18n.t('builder.subscription_cancellation_v4.resume_distribution.enabled.check')}
        </PremiumButton>
        <CancelSubcriptionText onClick={onContinueCancellation}>
          {i18n.t('builder.subscription_cancellation_v4.resume_distribution.enabled.stop')}
        </CancelSubcriptionText>
      </ButtonsContainer>
    </Container>
  )
}
